// Polished, on-brand PDF for the Project Lifecycle Recap.
// Design goals: clean, modern, consulting-grade; app indigo as the single brand
// accent; emerald/red/amber reserved strictly for determination status; ink/
// slate/gray for all text; KPI tiles as hero numbers, meters with rounded ends,
// soft-header tables. NO fiscal figures. Avoids non-WinAnsi glyphs (→, ≥) that
// jsPDF's core fonts can't encode — uses en-dash / middle-dot / ">=" instead.

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { ProjectLifecycleReport } from '../services/reporting';

type RGB = [number, number, number];

const C = {
  ink: [15, 23, 42] as RGB,
  slate: [51, 65, 85] as RGB,
  mute: [100, 116, 139] as RGB,
  faint: [148, 163, 184] as RGB,
  line: [226, 232, 240] as RGB,
  hair: [237, 240, 244] as RGB,
  soft: [248, 250, 252] as RGB,
  soft2: [241, 245, 249] as RGB,
  zebra: [250, 251, 253] as RGB,
  indigo: [79, 70, 229] as RGB,
  indigoInk: [67, 56, 202] as RGB,
  indigoTint: [238, 238, 252] as RGB,
  indigoText: [214, 213, 250] as RGB,
  emer: [16, 185, 129] as RGB,
  emerTint: [230, 248, 240] as RGB,
  red: [239, 68, 68] as RGB,
  amber: [245, 158, 11] as RGB,
  white: [255, 255, 255] as RGB,
};

const detColor = (d: string): RGB => (d === 'valid' ? C.emer : d === 'invalid' ? C.red : C.amber);
const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const stLabel = (s: string) => (s === 'dnc' ? 'Do Not Contact' : cap(String(s)));

// jsPDF's core fonts are WinAnsi-only; Unicode arrows/quotes/dashes render as
// garbage. Replace them with safe equivalents for any dynamic text we draw.
// (The on-screen HTML report keeps the pretty glyphs — this is PDF-only.)
const safe = (s: any): string =>
  String(s ?? '')
    .replace(/\s*→\s*/g, ' to ')
    .replace(/\s*≥\s*/g, ' >= ')
    .replace(/\s*≤\s*/g, ' <= ')
    .replace(/—/g, '-')
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/…/g, '...')
    .replace(/•/g, '-');

async function fetchMapDataUrl(url: string): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const cv = document.createElement('canvas');
        cv.width = img.naturalWidth;
        cv.height = img.naturalHeight;
        cv.getContext('2d')!.drawImage(img, 0, 0);
        resolve(cv.toDataURL('image/jpeg', 0.9));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

export async function buildLifecyclePdf(
  pdf: jsPDF,
  report: ProjectLifecycleReport,
  apiKey: string
): Promise<void> {
  const { summary, leads, geoPoints } = report;
  const W = pdf.internal.pageSize.getWidth();
  const H = pdf.internal.pageSize.getHeight();
  const M = 15;
  const CW = W - M * 2;
  const BOTTOM = H - 14;
  let y = 0;

  // ---- primitives ----
  const fill = (c: RGB) => pdf.setFillColor(c[0], c[1], c[2]);
  const stroke = (c: RGB) => pdf.setDrawColor(c[0], c[1], c[2]);
  const ink = (c: RGB) => pdf.setTextColor(c[0], c[1], c[2]);
  const font = (style: 'normal' | 'bold', size: number) => {
    pdf.setFont('helvetica', style);
    pdf.setFontSize(size);
  };
  const lastY = () => (pdf as any).lastAutoTable?.finalY ?? y;

  const runningHeader = (pageNum: number, total: number) => {
    fill(C.indigo);
    pdf.rect(0, 0, W, 12, 'F');
    font('bold', 8.5);
    ink(C.white);
    pdf.text('CANVASSED', M, 7.6);
    font('normal', 8);
    ink(C.indigoText);
    pdf.text('Project Lifecycle Recap', M + 27, 7.6);
    pdf.text(`Page ${pageNum} of ${total}`, W - M, 7.6, { align: 'right' });
  };

  const newPage = () => {
    pdf.addPage();
    y = 22;
  };
  const ensure = (need: number) => {
    if (y + need > BOTTOM) newPage();
  };

  const sectionTitle = (t: string) => {
    ensure(12);
    font('bold', 12.5);
    ink(C.ink);
    pdf.text(t, M, y);
    const w = Math.min(pdf.getTextWidth(t), 46);
    fill(C.indigo);
    pdf.rect(M, y + 1.6, w, 0.9, 'F');
    y += 8;
  };

  const softTable = (
    title: string,
    head: string[],
    body: (string | number)[][],
    columnStyles: any,
    tableWidth?: number
  ) => {
    sectionTitle(title);
    autoTable(pdf, {
      startY: y,
      head: [head],
      body: body.map((r) => r.map((c) => safe(c))),
      theme: 'striped',
      headStyles: {
        fillColor: C.soft2,
        textColor: C.slate,
        fontStyle: 'bold',
        fontSize: 8,
        cellPadding: { top: 2.4, bottom: 2.4, left: 3, right: 3 },
      },
      styles: {
        fontSize: 8.8,
        cellPadding: { top: 2.3, bottom: 2.3, left: 3, right: 3 },
        textColor: C.slate,
        lineColor: C.hair,
        lineWidth: 0.1,
      },
      alternateRowStyles: { fillColor: C.zebra },
      columnStyles,
      margin: { top: 16, left: M, right: M },
      tableWidth: tableWidth ?? 'auto',
    });
    y = lastY() + 9;
  };

  // ===================== COVER =====================
  // (running header is stamped on every page in a final pass, below)
  y = 26;
  font('bold', 23);
  ink(C.ink);
  pdf.text('Project Lifecycle Recap', M, y);
  y += 8.5;
  font('normal', 13);
  ink(C.indigoInk);
  pdf.text(safe(summary.projectName), M, y);
  y += 6.4;
  font('normal', 9);
  ink(C.mute);
  pdf.text(`${summary.dateRangeLabel}   ·   generated ${summary.generatedAtLabel}`, M, y);
  y += 4.5;
  pdf.text(`Field activity ${summary.firstLeadLabel} – ${summary.lastLeadLabel}`, M, y);
  y += 7;
  stroke(C.line);
  pdf.setLineWidth(0.4);
  pdf.line(M, y, W - M, y);
  y += 8;

  // KPI tiles — unit follows the project's counting mode (households for
  // signature projects, students otherwise).
  const hh = (summary as any).unitMode === 'households';
  const tiles: { num: number; l1: string; l2: string; dot: RGB }[] = hh
    ? [
        { num: summary.totalLeads, l1: 'HOUSEHOLDS', l2: 'total', dot: C.indigo },
        { num: summary.leadsByDetermination.valid, l1: 'SIGNED / VALID', l2: 'households', dot: C.emer },
        { num: summary.leadsByDetermination.invalid, l1: 'INVALID', l2: 'households', dot: C.red },
        { num: summary.leadsByDetermination.pending, l1: 'PENDING', l2: 'households', dot: C.amber },
        { num: summary.totalStudents, l1: 'STUDENTS', l2: 'listed', dot: C.slate },
      ]
    : [
    { num: summary.totalLeads, l1: 'PARENTS', l2: 'households', dot: C.indigo },
    { num: summary.totalStudents, l1: 'STUDENTS', l2: 'children', dot: C.slate },
    { num: summary.studentsByDetermination.valid, l1: 'VALID', l2: 'students', dot: C.emer },
    { num: summary.studentsByDetermination.invalid, l1: 'INVALID', l2: 'students', dot: C.red },
    { num: summary.studentsByDetermination.pending, l1: 'PENDING', l2: 'students', dot: C.amber },
    { num: summary.enrolledStudents, l1: 'ENROLLED', l2: 'students', dot: C.indigo },
  ];
  const n = tiles.length;
  const gap = 3;
  const tw = (CW - gap * (n - 1)) / n;
  const th = 21;
  tiles.forEach((t, i) => {
    const x = M + i * (tw + gap);
    fill(C.soft);
    stroke(C.line);
    pdf.setLineWidth(0.3);
    pdf.roundedRect(x, y, tw, th, 1.6, 1.6, 'FD');
    fill(t.dot);
    pdf.circle(x + tw / 2, y + 4, 1, 'F');
    font('bold', 15);
    ink(C.ink);
    pdf.text(String(t.num), x + tw / 2, y + 11.5, { align: 'center' });
    font('bold', 6.4);
    ink(C.mute);
    pdf.text(t.l1, x + tw / 2, y + 15.6, { align: 'center' });
    font('normal', 5.4);
    ink(C.faint);
    pdf.text(t.l2, x + tw / 2, y + 18.6, { align: 'center' });
  });
  y += th + 8;

  // Contract progress panel
  if (summary.contract) {
    const ct = summary.contract;
    const ph = 40;
    ensure(ph + 4);
    fill(C.soft);
    stroke(C.line);
    pdf.setLineWidth(0.3);
    pdf.roundedRect(M, y, CW, ph, 2.2, 2.2, 'FD');
    const px = M + 6;
    const pt = y + 8;
    font('bold', 8.5);
    ink(C.indigoInk);
    pdf.text('CONTRACT PROGRESS', px, pt);
    // status pill
    const satisfied = ct.contractSatisfied;
    const pillTxt = satisfied
      ? 'CONTRACT SATISFIED'
      : `${ct.enrollmentGoal - ct.enrollmentsCurrent} ENROLLMENTS TO SATISFY`;
    font('bold', 7);
    const ptw = pdf.getTextWidth(pillTxt) + 8;
    fill(satisfied ? C.emerTint : C.indigoTint);
    pdf.roundedRect(W - M - 6 - ptw, pt - 4.4, ptw, 6.2, 3.1, 3.1, 'F');
    ink(satisfied ? C.emer : C.indigoInk);
    pdf.text(pillTxt, W - M - 6 - ptw / 2, pt, { align: 'center' });

    const cy = y + 16;
    // Meters with a zero goal/target are dropped (e.g. signature projects have
    // no enrollment goal or conversion requirement).
    const cols = [
      ...(ct.enrollmentGoal > 0 ? [{
        label: hh ? 'ENROLLED HOUSEHOLDS' : 'ENROLLED STUDENTS',
        val: `${ct.enrollmentsCurrent}`,
        goal: `/ ${ct.enrollmentGoal}`,
        pct: ct.enrollmentGoal ? (ct.enrollmentsCurrent / ct.enrollmentGoal) * 100 : 0,
        sub: 'Reaching the target satisfies the contract',
        done: satisfied,
      }] : []),
      {
        label: hh ? 'SIGNED HOUSEHOLDS' : 'VALID STUDENTS',
        val: `${ct.leadsCurrent}`,
        goal: `/ ${ct.leadGoal}`,
        pct: ct.leadGoal ? (ct.leadsCurrent / ct.leadGoal) * 100 : 0,
        sub: hh ? 'Signature target' : 'Still required in full',
        done: ct.leadsCurrent >= ct.leadGoal,
      },
      ...(ct.conversionTarget > 0 ? [{
        label: 'CONVERSION',
        val: `${ct.conversionCurrent}%`,
        goal: '',
        pct: ct.conversionTarget ? (ct.conversionCurrent / ct.conversionTarget) * 100 : 0,
        sub: `Requirement: ${ct.conversionTarget}% or higher  ·  ${ct.conversionMet ? 'met' : 'below target'}`,
        done: ct.conversionMet,
      }] : []),
    ];
    const colW = (CW - 12) / cols.length;
    cols.forEach((c, i) => {
      const cx = px + i * colW;
      font('normal', 6.6);
      ink(C.mute);
      pdf.text(c.label, cx, cy);
      font('bold', 15);
      ink(C.ink);
      pdf.text(c.val, cx, cy + 7.5);
      if (c.goal) {
        const vw = pdf.getTextWidth(c.val);
        font('normal', 8.5);
        ink(C.faint);
        pdf.text(` ${c.goal}`, cx + vw + 1, cy + 7.5);
      }
      const barW = colW - 8;
      const barY = cy + 11.5;
      fill(C.line);
      pdf.roundedRect(cx, barY, barW, 2.4, 1.2, 1.2, 'F');
      const fc = c.done ? C.emer : C.indigo;
      const pw = Math.max(c.pct > 0 ? 2.4 : 0, (barW * Math.min(100, c.pct)) / 100);
      if (pw > 0) {
        fill(fc);
        pdf.roundedRect(cx, barY, pw, 2.4, 1.2, 1.2, 'F');
      }
      font('normal', 6.2);
      ink(C.faint);
      pdf.text(pdf.splitTextToSize(c.sub, barW), cx, barY + 5);
    });
    y += ph + 5;

    // enrollment note (student-counted projects only)
    if (!hh) {
      font('normal', 7);
      ink(C.mute);
      const note = pdf.splitTextToSize(
        "Enrollments are counted per student, assuming every student in an enrolled family enrolled. Please alert us of any students who did not enroll and we'll remove them from our records.",
        CW
      );
      pdf.text(note, M, y);
      y += note.length * 3.3 + 7;
    } else {
      y += 4;
    }
  }

  // Parent map (best-effort)
  let mapUrl: string | null = null;
  if (apiKey && geoPoints.length) {
    const groups: Record<string, string[]> = { valid: [], invalid: [], pending: [] };
    geoPoints.forEach((p) => {
      if (groups[p.determination]) groups[p.determination].push(`${p.lat.toFixed(5)},${p.lng.toFixed(5)}`);
    });
    const colorHex: Record<string, string> = { valid: '0x10B981', invalid: '0xEF4444', pending: '0xF59E0B' };
    const markerParams = Object.keys(groups)
      .filter((d) => groups[d].length)
      .map((d) => `markers=size:tiny|color:${colorHex[d]}|${groups[d].join('|')}`);
    const url = `https://maps.googleapis.com/maps/api/staticmap?size=640x300&scale=2&maptype=roadmap&${markerParams.join('&')}&key=${apiKey}`;
    try {
      mapUrl = await fetchMapDataUrl(url);
    } catch {
      mapUrl = null;
    }
  }
  if (mapUrl) {
    const imgH = (CW * 300) / 640;
    ensure(imgH + 16);
    sectionTitle('Parent Map');
    stroke(C.line);
    pdf.setLineWidth(0.3);
    pdf.roundedRect(M, y, CW, imgH, 2, 2, 'S');
    pdf.addImage(mapUrl, 'JPEG', M + 0.6, y + 0.6, CW - 1.2, imgH - 1.2);
    y += imgH + 4;
    font('normal', 7);
    ink(C.mute);
    pdf.text(
      `Valid (green)   ·   Invalid (red)   ·   Pending (amber).    ${geoPoints.length} of ${summary.totalLeads} parents located.`,
      M,
      y
    );
    y += 8;
  }

  // Breakdown tables
  softTable(
    'By Campus',
    ['Campus', 'Parents', 'Students', 'Valid', 'Enrolled'],
    summary.byCampus.map((c) => [c.name, c.leads, c.students, c.valid, c.enrolled]),
    {
      1: { halign: 'right' },
      2: { halign: 'right' },
      3: { halign: 'right', textColor: C.emer, fontStyle: 'bold' },
      4: { halign: 'right' },
    }
  );
  softTable(
    'By Canvasser',
    ['Canvasser', 'Parents', 'Students', 'Valid students'],
    summary.byCanvasser.map((c) => [c.name, c.leads, c.students, c.validStudents]),
    {
      1: { halign: 'right' },
      2: { halign: 'right' },
      3: { halign: 'right', textColor: C.emer, fontStyle: 'bold' },
    }
  );
  const statusRows = (['uncontacted', 'cold', 'warm', 'hot', 'enrolled', 'lost', 'dnc'] as const)
    .filter((s) => (summary.leadsByStatus as any)[s] > 0)
    .map((s) => [stLabel(s), (summary.leadsByStatus as any)[s]]);
  if (statusRows.length) {
    softTable('By Status', ['Status', 'Parents'], statusRows, { 1: { halign: 'right' } }, 92);
  }

  // Data-quality notes
  if (summary.dataFlags.length) {
    const boxH = 9 + summary.dataFlags.reduce((a, f) => a + pdf.splitTextToSize(f, CW - 14).length * 4, 0);
    ensure(boxH + 4);
    fill([255, 251, 235]);
    stroke([253, 230, 138]);
    pdf.setLineWidth(0.3);
    pdf.roundedRect(M, y, CW, boxH, 2, 2, 'FD');
    let ny = y + 6;
    font('bold', 8.5);
    ink([146, 64, 14]);
    pdf.text('DATA-QUALITY NOTES', M + 5, ny);
    ny += 5;
    font('normal', 8);
    ink([120, 53, 15]);
    summary.dataFlags.forEach((f) => {
      const wrapped = pdf.splitTextToSize(safe(`-  ${f}`), CW - 14);
      pdf.text(wrapped, M + 5, ny);
      ny += wrapped.length * 4;
    });
    y += boxH + 6;
  }

  // ===================== PER-LEAD =====================
  // Continue on the current page when there's room for the heading + first
  // entry (avoids a mostly-empty page); break only if it won't fit.
  ensure(34);
  y += 4;
  font('bold', 15);
  ink(C.ink);
  pdf.text('Lead-by-Lead Breakdown', M, y);
  const tw2 = pdf.getTextWidth('Lead-by-Lead Breakdown');
  fill(C.indigo);
  pdf.rect(M, y + 1.8, Math.min(tw2, 52), 0.9, 'F');
  y += 6;
  font('normal', 8.5);
  ink(C.mute);
  pdf.text(`${leads.length} parents  ·  ordered valid, then invalid, then pending`, M, y);
  y += 8;

  // Prefetch per-lead mini-maps for signature leads: home (H) → signing spot
  // (S) with a connecting line — drawn beside each signature block.
  const sigMaps = new Map<string, string>();
  if (apiKey) {
    await Promise.all(
      leads
        .filter((l) => l.signature && typeof l.lat === 'number' && typeof l.lng === 'number' && typeof l.signatureLat === 'number' && typeof l.signatureLng === 'number')
        .map(async (l) => {
          const url =
            `https://maps.googleapis.com/maps/api/staticmap?size=300x140&scale=2&maptype=roadmap` +
            `&markers=size:small%7Ccolor:0x4F46E5%7Clabel:H%7C${l.lat},${l.lng}` +
            `&markers=size:small%7Ccolor:0x059669%7Clabel:S%7C${l.signatureLat},${l.signatureLng}` +
            `&path=color:0x4F46E5CC%7Cweight:3%7C${l.lat},${l.lng}%7C${l.signatureLat},${l.signatureLng}` +
            `&key=${apiKey}`;
          const data = await fetchMapDataUrl(url);
          if (data) sigMaps.set(l.id, data);
        })
    );
  }

  leads.forEach((lead, idx) => {
    const dc = detColor(lead.determination);
    ensure(24);

    // name + determination chip + status
    font('bold', 11);
    ink(C.ink);
    pdf.text(safe(`${idx + 1}.  ${lead.parentName}`), M, y);
    const chip = lead.determination.toUpperCase();
    font('bold', 6.8);
    const cw2 = pdf.getTextWidth(chip) + 6;
    fill(dc);
    pdf.roundedRect(W - M - cw2, y - 3.8, cw2, 5.4, 2.7, 2.7, 'F');
    ink(C.white);
    pdf.text(chip, W - M - cw2 / 2, y - 0.1, { align: 'center' });
    font('normal', 8);
    ink(C.mute);
    pdf.text(stLabel(lead.status), W - M - cw2 - 3, y - 0.1, { align: 'right' });
    y += 5;

    // info
    font('normal', 8.5);
    ink(C.slate);
    const addr = pdf.splitTextToSize(safe(lead.addressLine), CW);
    pdf.text(addr, M, y);
    y += addr.length * 3.9;
    font('normal', 8);
    ink(C.mute);
    pdf.text(
      safe(`${lead.campus}  ·  Captured by ${lead.canvasser}, ${lead.capturedLabel}  ·  ${lead.studentCount} student${lead.studentCount === 1 ? '' : 's'}`),
      M,
      y
    );
    y += 3.9;
    const contact = [lead.phone, lead.email, lead.language].filter(Boolean).join('   ·   ');
    if (contact) {
      pdf.text(safe(contact), M, y);
      y += 3.9;
    }

    // students
    y += 1;
    lead.students.forEach((s) => {
      ensure(4.5);
      const sc = detColor(s.determination);
      fill(sc);
      pdf.circle(M + 2, y - 1, 0.7, 'F');
      font('normal', 8);
      ink(C.slate);
      const grade = !s.grade || s.grade === '—' || s.grade === '-' ? 'grade -' : `${s.grade} grade`;
      pdf.text(
        pdf.splitTextToSize(safe(`${s.name}  ·  ${grade}  ·  ${cap(s.determination)} / ${stLabel(s.status)}`), CW - 6),
        M + 5,
        y
      );
      y += 4;
    });

    // why — colored lead-in label, then the reason with a hanging indent
    const whyLabel = `Why ${cap(lead.determination)}: `;
    y += 1.5;
    font('bold', 8);
    const wlw = pdf.getTextWidth(whyLabel);
    const reasonLines = pdf.splitTextToSize(safe(lead.determinationReason), CW - wlw);
    ensure(reasonLines.length * 3.9 + 3);
    ink(dc);
    pdf.text(whyLabel, M, y);
    font('normal', 8);
    ink(C.slate);
    pdf.text(reasonLines, M + wlw, y);
    y += reasonLines.length * 3.9 + 1.5;

    // interest signature + submission GPS (signature projects) — the per-lead
    // accountability record: signature image, signing time, collector position.
    if (lead.signature) {
      ensure(20);
      y += 1;
      const sw = 42, sh = 14;
      stroke(C.line);
      fill(C.white);
      pdf.roundedRect(M, y, sw, sh, 1.5, 1.5, 'FD');
      try { pdf.addImage(lead.signature, 'PNG', M + 1, y + 1, sw - 2, sh - 2); } catch { /* keep the empty box */ }
      font('bold', 7.4);
      ink(C.slate);
      pdf.text('SIGNATURE OF INTEREST', M + sw + 4, y + 3);
      font('normal', 7.4);
      ink(C.mute);
      const sigLines: string[] = [];
      if (lead.signatureCapturedAt) sigLines.push(`Signed ${lead.signatureCapturedAt.toLocaleString('en-US')}`);
      sigLines.push(lead.signatureLat != null && lead.signatureLng != null
        ? `Submitted at ${lead.signatureLat.toFixed(6)}, ${lead.signatureLng.toFixed(6)}${lead.signatureAccuracy != null ? ` (+/- ${Math.round(lead.signatureAccuracy)}m)` : ''}`
        : 'Submission GPS not recorded');
      sigLines.push(`${lead.consentToContact ? 'Consented to follow-up contact' : 'No contact consent given'}${lead.howHeard ? `  ·  Heard via: ${lead.howHeard}` : ''}${lead.isBusiness ? `  ·  Business${lead.businessName ? `: ${lead.businessName}` : ''}${lead.businessRole ? ` (signer: ${lead.businessRole})` : ''}${lead.businessAddress ? ` — ${lead.businessAddress}` : ''}` : ''}`);
      sigLines.forEach((t, i) => pdf.text(safe(t), M + sw + 4, y + 6.5 + i * 3.4));
      // Mini-map at the right edge: home (H) with a line to the signing spot (S).
      const sigMap = sigMaps.get(lead.id);
      if (sigMap) {
        const mw = 30, mh = 14;
        try {
          pdf.addImage(sigMap, 'PNG', W - M - mw, y, mw, mh);
          stroke(C.line);
          pdf.rect(W - M - mw, y, mw, mh, 'S');
        } catch { /* map optional */ }
      }
      y += sh + 3;

      // The exact affirmation + consent language this signer agreed to.
      const sAny: any = summary;
      const verbatim: string[] = [];
      if (sAny.signatureAttestation) verbatim.push(`Affirmed at signing: "${sAny.signatureAttestation}"`);
      if (lead.consentToContact && sAny.consentText) verbatim.push(`Consent given: "${sAny.consentText}"`);
      if (!lead.consentToContact) verbatim.push('No contact consent given.');
      verbatim.forEach((t) => {
        const lines = pdf.splitTextToSize(safe(t), CW);
        ensure(lines.length * 2.9 + 2);
        font('normal', 6.4);
        ink(C.faint);
        pdf.text(lines, M, y);
        y += lines.length * 2.9 + 1.2;
      });
      y += 1;
    }

    // timeline
    ensure(5);
    font('bold', 6.6);
    ink(C.faint);
    pdf.text('TIMELINE', M, y);
    y += 3.6;
    lead.timeline.forEach((e) => {
      ensure(4);
      font('normal', 7.6);
      ink(C.faint);
      pdf.text(safe(e.dateLabel), M + 2, y);
      ink(C.slate);
      const detail = e.detail ? `  ·  ${e.detail}` : '';
      const txt = pdf.splitTextToSize(safe(`${e.label}${detail}`), CW - 30);
      pdf.text(txt, M + 26, y);
      y += Math.max(3.6, txt.length * 3.6);
    });

    // meta
    const meta: string[] = [];
    if (lead.contactAttempts) meta.push(`${lead.contactAttempts} contact attempt${lead.contactAttempts === 1 ? '' : 's'}`);
    if (lead.lastContactedLabel) meta.push(`last contacted ${lead.lastContactedLabel}`);
    if (lead.appointments.length) meta.push(`${lead.appointments.length} tour${lead.appointments.length === 1 ? '' : 's'}`);
    if (meta.length) {
      ensure(4);
      y += 0.5;
      font('normal', 7.4);
      ink(C.faint);
      pdf.text(meta.join('   ·   '), M + 2, y);
      y += 3.6;
    }

    // divider
    ensure(5);
    y += 1.5;
    stroke(C.hair);
    pdf.setLineWidth(0.3);
    pdf.line(M, y, W - M, y);
    y += 5;
  });

  // Stamp the running header band on EVERY page (covers autoTable's own page
  // breaks, which don't run newPage()).
  const total = (pdf.internal as any).getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    pdf.setPage(i);
    runningHeader(i, total);
  }
}
